import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import {
  RefreshCw,
  Play,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  ChevronRight,
  Filter,
  BarChart3,
  Activity,
  ArrowUpRight,
} from 'lucide-react';
import { RunNowButton } from '../components/RunNowButton';

interface ReminderRun {
  id: number;
  site_id: number | null;
  policy_id: number | null;
  run_type: 'scheduled' | 'manual';
  status: 'running' | 'queued' | 'completed' | 'failed';
  started_at: string;
  finished_at: string | null;
  total_candidates: number;
  total_queued: number;
  total_sent: number;
  total_skipped: number;
  total_failed: number;
}

interface QueueStats {
  [key: string]: {
    waiting: number;
    active: number;
    completed: number;
    failed: number;
    delayed: number;
  };
}

interface PaymentRecoveryStats {
  period_days: number;
  total_payment_events: number;
  by_source: Record<string, number>;
  paid_invoices: number;
  thank_you_sent: number;
  paid_after_reminder: number;
  recovery_rate: number;
}

export function RemindersPage() {
  const navigate = useNavigate();
  const [runs, setRuns] = useState<ReminderRun[]>([]);
  const [queueStats, setQueueStats] = useState<QueueStats | null>(null);
  const [recoveryStats, setRecoveryStats] = useState<PaymentRecoveryStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'scheduled' | 'manual'>('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchData = async () => {
    try {
      const [runsResponse, queueResponse, recoveryResponse] = await Promise.all([
        axios.get(
          `${import.meta.env.VITE_API_URL}/api/reminders/runs?page=${page}&per_page=10`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
          }
        ),
        axios.get(`${import.meta.env.VITE_API_URL}/api/reminders/queue-stats`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }),
        axios.get(
          `${import.meta.env.VITE_API_URL}/api/reminders/payment-recovery-stats?days=30`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
          }
        ),
      ]);

      if (runsResponse.data.success) {
        setRuns(runsResponse.data.data.runs);
        setTotalPages(runsResponse.data.data.pagination.total_pages);
      }

      if (queueResponse.data.success) {
        setQueueStats(queueResponse.data.data);
      }

      if (recoveryResponse.data.success) {
        setRecoveryStats(recoveryResponse.data.data);
      }
    } catch (error) {
      toast.error('Failed to load reminders data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [page]);

  const filteredRuns = runs.filter((run) => {
    if (filter === 'all') return true;
    return run.run_type === filter;
  });

  const getStatusBadge = (status: string) => {
    const statusClasses: Record<string, string> = {
      running: 'bg-blue-100 text-blue-800',
      queued: 'bg-yellow-100 text-yellow-800',
      completed: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
    };

    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          statusClasses[status] || 'bg-gray-100 text-gray-800'
        }`}
      >
        {status === 'running' && <RefreshCw className="w-3 h-3 mr-1 animate-spin" />}
        {status === 'queued' && <Clock className="w-3 h-3 mr-1" />}
        {status === 'completed' && <CheckCircle className="w-3 h-3 mr-1" />}
        {status === 'failed' && <XCircle className="w-3 h-3 mr-1" />}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const getSuccessRate = (run: ReminderRun) => {
    if (run.total_candidates === 0) return 0;
    return ((run.total_sent / run.total_candidates) * 100).toFixed(1);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Reminder Runs</h1>
          <p className="text-gray-500 mt-1">
            Manage and monitor WhatsApp invoice reminders
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={fetchData}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
          <RunNowButton onRunStarted={() => fetchData()} />
        </div>
      </div>

      {/* Queue Stats */}
      {queueStats && (
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {Object.entries(queueStats).map(([queueName, stats]) => (
            <div key={queueName} className="bg-white rounded-lg shadow p-4">
              <h4 className="text-sm font-medium text-gray-500 capitalize">
                {queueName.replace(/-/g, ' ')}
              </h4>
              <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                <div className="text-blue-600">
                  <span className="font-semibold">{stats.waiting}</span> waiting
                </div>
                <div className="text-green-600">
                  <span className="font-semibold">{stats.active}</span> active
                </div>
                <div className="text-gray-600">
                  <span className="font-semibold">{stats.completed}</span> done
                </div>
                <div className="text-red-600">
                  <span className="font-semibold">{stats.failed}</span> failed
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Payment Recovery Stats */}
      {recoveryStats && (
        <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-6">
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 className="w-5 h-5 text-green-600" />
            <h3 className="text-lg font-semibold">Payment Recovery Stats (Last 30 Days)</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <RecoveryStatCard
              label="Payment Events"
              value={recoveryStats.total_payment_events}
              icon={<Activity className="w-4 h-4" />}
              trend={null}
            />
            <RecoveryStatCard
              label="Paid Invoices"
              value={recoveryStats.paid_invoices}
              icon={<CheckCircle className="w-4 h-4" />}
              trend={null}
            />
            <RecoveryStatCard
              label="Paid After Reminder"
              value={recoveryStats.paid_after_reminder}
              icon={<ArrowUpRight className="w-4 h-4" />}
              trend={
                recoveryStats.paid_invoices > 0
                  ? ((recoveryStats.paid_after_reminder / recoveryStats.paid_invoices) * 100).toFixed(1) + '%'
                  : '0%'
              }
            />
            <RecoveryStatCard
              label="Thank You Sent"
              value={recoveryStats.thank_you_sent}
              icon={<Clock className="w-4 h-4" />}
              trend={null}
            />
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-500" />
          <span className="text-sm text-gray-500">Filter:</span>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as any)}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
          >
            <option value="all">All Runs</option>
            <option value="scheduled">Scheduled</option>
            <option value="manual">Manual</option>
          </select>
        </div>
      </div>

      {/* Runs Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                ID
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Type
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Started
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Candidates
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Sent / Failed / Skipped
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Success Rate
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {filteredRuns.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-6 py-8 text-center text-gray-500">
                  <AlertCircle className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No reminder runs found</p>
                  <p className="text-sm mt-1">Start a new run to see results here</p>
                </td>
              </tr>
            ) : (
              filteredRuns.map((run) => (
                <tr key={run.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-medium">#{run.id}</td>
                  <td className="px-6 py-4">{getStatusBadge(run.status)}</td>
                  <td className="px-6 py-4">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        run.run_type === 'scheduled'
                          ? 'bg-purple-100 text-purple-800'
                          : 'bg-orange-100 text-orange-800'
                      }`}
                    >
                      {run.run_type === 'scheduled' ? (
                        <Clock className="w-3 h-3 mr-1" />
                      ) : (
                        <Play className="w-3 h-3 mr-1" />
                      )}
                      {run.run_type.charAt(0).toUpperCase() + run.run_type.slice(1)}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {format(new Date(run.started_at), 'MMM d, yyyy HH:mm')}
                  </td>
                  <td className="px-6 py-4 text-sm">{run.total_candidates}</td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-green-600 font-medium">{run.total_sent}</span>
                      <span className="text-gray-400">/</span>
                      <span className="text-red-600 font-medium">{run.total_failed}</span>
                      <span className="text-gray-400">/</span>
                      <span className="text-yellow-600 font-medium">{run.total_skipped}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <div className="w-16 bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-green-500 h-2 rounded-full"
                          style={{ width: `${getSuccessRate(run)}%` }}
                        />
                      </div>
                      <span className="text-sm text-gray-600">{getSuccessRate(run)}%</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <button
                      onClick={() => navigate(`/reminders/runs/${run.id}`)}
                      className="inline-flex items-center text-blue-600 hover:text-blue-800 text-sm font-medium"
                    >
                      Details
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500">
            Page {page} of {totalPages}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-1 border border-gray-300 rounded-lg text-sm disabled:opacity-50"
            >
              Previous
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-3 py-1 border border-gray-300 rounded-lg text-sm disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function RecoveryStatCard({
  label,
  value,
  icon,
  trend,
}: {
  label: string;
  value: number;
  icon: React.ReactNode;
  trend: string | null;
}) {
  return (
    <div className="bg-white rounded-lg p-4 shadow-sm">
      <div className="flex items-center justify-between">
        <div className="text-gray-500">{icon}</div>
        {trend && (
          <span className="text-xs font-medium text-green-600 flex items-center">
            <ArrowUpRight className="w-3 h-3 mr-0.5" />
            {trend}
          </span>
        )}
      </div>
      <div className="mt-2">
        <p className="text-2xl font-bold text-gray-900">{value}</p>
        <p className="text-sm text-gray-500">{label}</p>
      </div>
    </div>
  );
}
